import { Component, inject, OnInit, signal, HostListener, ElementRef, LOCALE_ID, input, Input } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { SuppliersService } from '../../../../services/suppliers/suppliers.service';
import { StockService } from '../../../../services/product/stock/stock';
import { ProductService } from '../../../../services/product/product.service';
import {
  ProductAutocompleteItem,
  SupplierItem,
  StockIncomingItem,
  AdminSuppliersResponseBody,
} from '../../../../pages/admin-dashboard/stock-management/add-all-stock/add-stock.model';
import { ProductPrices } from '../../add-product-component/steps/product-info-step/product-prices/product-prices';

@Component({
  selector: 'app-add-stock-form',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, ProductPrices],
  templateUrl: './add-stock-form.html',
    styleUrl: './add-stock-form.scss',
})
export class AddStockForm implements OnInit {
  private locale = inject(LOCALE_ID);
  private router = inject(Router);
  private stockService = inject(StockService);
  private productService = inject(ProductService);
  private suppliersService = inject(SuppliersService);
  private eRef = inject(ElementRef);

  productId = input<number | null>(null);
  suppliers = signal<SupplierItem[]>([]);
  productsAutocomplete = signal<ProductAutocompleteItem[]>([]);
  temporaryStockList = signal<StockIncomingItem[]>([]);
  showProductAutocomplete = signal<boolean>(false);

  stockForm = new FormGroup({
    productId: new FormControl<number | null>(null, Validators.required),
    productSearch: new FormControl('', Validators.required),
    supplierId: new FormControl<number | null>(null, Validators.required),
    quantity: new FormControl<number | null>(null, [Validators.required, Validators.min(1)]),
    peoplePrice: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(0.1),
      Validators.max(1000000),
    ]),
    sellerDiscount: new FormControl<number | null>(0, [Validators.required, Validators.min(0), Validators.max(100)]),
    sellerPrice: new FormControl({ value: 0, disabled: true }),
    pharmacyDiscount: new FormControl<number | null>(0, [Validators.required, Validators.min(0), Validators.max(100)]),
    pharmacyPrice: new FormControl({ value: 0, disabled: true }),
    batchNumber: new FormControl('', [Validators.required, Validators.pattern('^[a-zA-Z0-9-]+$')]),
    mfgDate: new FormControl('', Validators.required),
    expDate: new FormControl('', Validators.required),
  });

  

  @HostListener('document:click', ['$event'])
  clickout(event: MouseEvent) {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.showProductAutocomplete.set(false);
    }
  }

  ngOnInit() {
    this.loadSuppliers();
    this.setupPriceCalculation();
  }

  loadSuppliers() {
    this.suppliersService.getAllSuppliers({ pageSize: 100, isActive: true }).subscribe({
      next: (res: AdminSuppliersResponseBody) => this.suppliers.set(res.list),
    });
  }

  setupPriceCalculation() {
    this.stockForm.valueChanges.subscribe((val) => {
      const p = val.peoplePrice || 0;
      const sD = val.sellerDiscount || 0;
      const pD = val.pharmacyDiscount || 0;

      this.stockForm.patchValue(
        {
          sellerPrice: Number((p - (p * sD) / 100).toFixed(2)),
          pharmacyPrice: Number((p - (p * pD) / 100).toFixed(2)),
        },
        { emitEvent: false },
      );
    });
  }

  onProductSearch(event: Event) {
    const term = (event.target as HTMLInputElement).value;
    if (term.length >= 2) {
      this.productService.autocompleteAdminProducts(term).subscribe((res) => {
        this.productsAutocomplete.set(res.list);
        this.showProductAutocomplete.set(true);
      });
    }
  }

  selectProduct(prod: ProductAutocompleteItem) {
    this.stockForm.patchValue({
      productId: prod.id,
      productSearch: this.getTranslatedTitle(prod),
      peoplePrice: prod.peoplePrice ?? null,
      sellerDiscount: prod.sellerPriceDiscountPercentage ?? 0,
      pharmacyDiscount: prod.pharmacyPriceDiscountPercentage ?? 0,
    });
    this.showProductAutocomplete.set(false);
  }

  getTranslatedTitle(item: ProductAutocompleteItem | SupplierItem): string {
    if (this.locale.startsWith('ar')) return item.titleAr ?? item.titleEn ?? '';
    return item.titleEn ?? item.titleAr ?? '';
  }

  addToList() {
    const val = this.stockForm.getRawValue();

    if (val.sellerDiscount! < val.pharmacyDiscount!) {
      return;
    }

    if (this.stockForm.valid) {
      const newItem: StockIncomingItem = {
        productId: val.productId!,
        supplierId: Number(val.supplierId),
        quantity: val.quantity!,
        peoplePrice: val.peoplePrice!,
        sellerPriceDiscountPercentage: val.sellerDiscount!,
        pharmacyPriceDiscountPercentage: val.pharmacyDiscount!,
        batchNumber: val.batchNumber!,
        mfgDate: val.mfgDate!,
        expDate: val.expDate!,
        productTitle: val.productSearch!,
        id: Date.now(),
      };

      this.temporaryStockList.update((list) => [...list, newItem]);
      this.stockForm.reset({ supplierId: val.supplierId });
    } else {
      this.stockForm.markAllAsTouched();
    }
  }

  removeItem(id: number) {
    this.temporaryStockList.update((list) => list.filter((item) => item.id !== id));
  }

  submitAll() {
    const finalData = {
      list: this.temporaryStockList().map(({ id, productTitle, ...rest }) => rest),
    };

    this.stockService.addBulkIncomingStock(finalData).subscribe({
      next: () => {
        this.temporaryStockList.set([]);
        this.router.navigate(['/admin/stocks']);
      },
    });
  }
}
